import { useMutation } from '@apollo/client'
import { SnackbarVariant } from '@focaldata/cin-ui-components'
import { SET_AUDIENCE_QUESTION_LOGIC_V2 } from '../../data/gql/questionnaire/mutations/audienceQuestionLogicV2'
import { draftQuestionnaireRefetchQuery } from '../../data/gql/questionnaire/queries'
import { audienceQuestionLogicRefetchQueries } from '../../data/gql/questionnaire/queries/audienceQuestionLogic'
import { draftAudienceRefetchQuery } from '../../data/gql/questionnaire/queries/audiences'
import { LoggerErrorType } from '../../data/logger'
import { DisplayLogicDraft } from '../../data/model/audienceQuestionLogic'
import { buildDisplayLogicCondition } from '../../modules/Audience/ScreenerDisplayLogic/screenerDisplayLogic.utils'
import { captureApolloError } from '../../utils/HelperFunctions'
import { useOptionalWaveId } from '../useOptionalWaveId'
import { useProjectId } from '../useProjectId'
import { useSafeEnqueueSnackbar } from '../useSafeEnqueueSnackbar'
import { useSurveyId } from '../useSurveyId'

// An empty draft clears the condition. Rejections come back as a plain mutation error, so surface them.
export const useSetAudienceQuestionLogicV2 = () => {
  const projectId = useProjectId()
  const surveyId = useSurveyId()
  const waveId = useOptionalWaveId()
  const enqueueSnackbar = useSafeEnqueueSnackbar()

  const [setAudienceQuestionLogic] = useMutation(
    SET_AUDIENCE_QUESTION_LOGIC_V2,
    {
      context: { clientName: 'questionnaire' },
      refetchQueries: [
        ...audienceQuestionLogicRefetchQueries(projectId, surveyId, waveId),
        draftAudienceRefetchQuery(projectId, surveyId, waveId),
        draftQuestionnaireRefetchQuery(projectId, surveyId, waveId)
      ],
      onError(error) {
        captureApolloError(
          LoggerErrorType.ApolloMutation,
          'SetAudienceQuestionLogicV2',
          error
        )
        enqueueSnackbar?.(`Couldn't save display logic. ${error.message}`, {
          variant: SnackbarVariant.Error
        })
      }
    }
  )

  return (memberId: string, draft: DisplayLogicDraft | undefined) =>
    setAudienceQuestionLogic({
      variables: {
        input: {
          projectId,
          surveyId,
          waveId,
          memberId,
          condition: draft
            ? buildDisplayLogicCondition(draft, surveyId).map((clause) => ({
                propositions: clause.map((proposition) => ({
                  audienceReponseOption: proposition.proposition,
                  negated: proposition.negated
                }))
              }))
            : []
        }
      }
    })
}
